import AvailableLayoutsCard from '@/components/layout/AvailableLayoutsCard';
import CardIconButton from '@/components/layout/CardIconButton';
import ColorIndicator from '@/components/layout/ColorIndicator';
import DisplayTypeCard from '@/components/layout/DisplayTypeCard';
import LayoutInfoCard from '@/components/layout/LayoutInfoCard';
import { LAYOUT_MODE, FullLayoutItem, LayoutDialogResult, LayoutList, COLOR_OPTION } from '@/types/pages/layout';
import { getStyleIds, parseSizeAndColorsFromXml } from '@/utils/layouts/layoutUtils';
import { Box, Chip, CircularProgress } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useEffect, useMemo, useRef } from 'react';
import LayoutDialog from '@/components/layout/LayoutDialog';
import { useLayoutState } from '@/provider/layouts/layoutContext';
import { useLayoutUtils } from '@/hooks/layouts/useLayoutUtils';
import { LAYOUT_TYPE } from '@/hooks/layouts/defaultState';
import { useTranslation } from 'react-i18next';

type MultiLayoutProps = {
    viewMode?: boolean;
};

const MultiLayout = ({ viewMode }: MultiLayoutProps) => {
    const { t } = useTranslation('common');
    const { state, dispatch } = useLayoutState();
    const { optionsWithLabel, formateMultiInfo } = useLayoutUtils();
    const {
        relations,
        displayType,
        styleSlug,
        styleId,
        layoutsById,
        searchSub,
        selectedColors,
        selectedLayouts,
        editLayout,
    } = useMemo(() => state.multi, [state.multi]);
    const nextTempId = useRef(1);

    useEffect(() => {
        let payload = null;
        if (state.layoutInfo) {
            const { layouts, relations, maxExisting } = formateMultiInfo();
            nextTempId.current = maxExisting + 1;
            payload = {
                layouts,
                relations,
            };
        }

        dispatch({ type: 'INIT_MULTI', payload });
    }, [state.layoutInfo, state.displayOptions]);

    const layoutNames = useMemo(() => Object.values(layoutsById).map((o) => o.name), [layoutsById]);

    const associatedLayouts = useMemo(() => {
        return getStyleIds(relations, displayType, styleSlug);
    }, [relations, displayType, styleSlug]);

    const handleReorder = (newList: LayoutList[]) => {
        dispatch({ type: 'REORDER_MULTI', newList });
    };

    const handleDetach = (ids: string[]) => {
        dispatch({ type: 'DETACH_MULTI', ids });
    };

    const defaultLayoutId = useMemo(() => {
        const { styleSlug, relations, displayType } = state.multi;
        if (!styleSlug) return null;
        return relations[displayType]?.[styleSlug]?.default_layout;
    }, [relations, displayType, styleSlug]);

    // Available
    const filteredLayouts = useMemo(() => {
        return Object.values(layoutsById).filter((layout) => {
            if (
                searchSub &&
                !layout.name.toLowerCase().includes(searchSub.toLowerCase()) &&
                !(layout.resolution as string).toLowerCase().includes(searchSub.toLowerCase())
            )
                return false;
            if (selectedColors.length > 0 && !selectedColors.includes(String(layout.bpp))) return false;
            return true;
        });
    }, [layoutsById, relations, displayType, searchSub, selectedColors]);

    const handleAttach = () => {
        const valid = filteredLayouts.filter((l) => selectedLayouts.includes(l.id));
        if (valid.length < 1) return;

        const attachIds = valid.map((l) => ({
            id: l.id,
            name: l.name,
            uuid: `multi-${l.id}-${crypto.randomUUID()}`,
        }));

        dispatch({
            type: 'SET_MULTI_RELATIONS',
            payload: {
                displayType: displayType,
                selectedList: attachIds,
                styleSlug: styleSlug as string,
            },
        });
        dispatch({ type: 'CLEAN_SELECTED_LAYOUT' });
    };

    const handleDeleteAll = () => {
        dispatch({ type: 'DELETE_SUB_LAYOUT_PACKAGE' });
    };

    const handleEditChange = (layout: FullLayoutItem) => (_event: React.MouseEvent) => {
        _event.stopPropagation();
        dispatch({
            type: 'UPDATE_EDIT_DATA',
            payload: layout,
        });
    };
    const editData = useMemo(() => {
        if (editLayout) {
            const { name, purpose, xmlContent } = editLayout;
            return {
                name,
                purpose: Number(purpose),
                content: xmlContent,
            };
        }
        return undefined;
    }, [editLayout]);

    const handleUpdateLayout = (updated: LayoutDialogResult) => {
        if (!editLayout) return;
        const { colors, bpp, resolution } = parseSizeAndColorsFromXml(updated.content);
        dispatch({
            type: 'STORE_EDIT_DATA',
            payload: {
                id: editLayout.id,
                colors,
                updated,
                bpp,
                resolution,
            },
        });
    };

    const handleAddNewLayout = ({ name, purpose, content }: LayoutDialogResult) => {
        const { colors, bpp, resolution } = parseSizeAndColorsFromXml(content);
        const newNumId = nextTempId.current++;
        const newIdStr = String(newNumId);

        const newLayout = {
            [newNumId]: {
                id: newIdStr,
                name,
                description: '',
                images: [],
                number: newIdStr,
                size: '',
                colors,
                purpose: String(purpose),
                xmlContent: content,
                bpp,
                resolution,
            },
        };
        dispatch({ type: 'ADD_NEW_SUB_LAYOUT', payload: newLayout });
    };
    const handleDeleteChange = (layout: FullLayoutItem) => (_event: React.MouseEvent) => {
        _event.stopPropagation();
        dispatch({
            type: 'DELETE_SUB_LAYOUT',
            payload: layout.id,
        });
    };
    const handleSetDefault = (uuid: string) => {
        dispatch({
            type: 'SET_MULTI_DEFAULT',
            payload: uuid,
        });
    };

    if (state.loadings.layout || state.loadings.category) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: { xs: 'block', md: 'grid' },
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                }}
            >
                <DisplayTypeCard
                    viewMode={viewMode}
                    mode={LAYOUT_MODE.MULTI}
                    options={optionsWithLabel}
                    value={displayType}
                    onChange={(dt) => dispatch({ type: 'SET_MULTI_DISPLAY', payload: dt })}
                    layouts={associatedLayouts}
                    onView={(id) => {
                        const el = document.getElementById(`layout-card-${LAYOUT_MODE.MULTI}-${id}`);
                        if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            el.classList.add('flash-highlight');
                            setTimeout(() => el.classList.remove('flash-highlight'), 600);
                        }
                    }}
                    selectStyleId={styleId}
                    setSelectStyleId={(id) => dispatch({ type: 'SET_STYLE_ID', styleId: id })}
                    onReorder={handleReorder}
                    onDetach={handleDetach}
                    onSetDefault={handleSetDefault}
                    defaultLayoutId={defaultLayoutId}
                />

                <AvailableLayoutsCard<FullLayoutItem>
                    viewMode={viewMode}
                    enableColor
                    tabType={LAYOUT_MODE.MULTI}
                    layouts={filteredLayouts}
                    selectedColors={selectedColors}
                    onColorChange={(newColors) => dispatch({ type: 'SET_SELECTED_COLORS', payload: newColors })}
                    colorOptions={COLOR_OPTION}
                    searchText={searchSub}
                    onSearchChange={(text) => dispatch({ type: 'SET_SEARCH_SUB', payload: text })}
                    onAttach={handleAttach}
                    onDeleteAll={handleDeleteAll}
                    onAddNew={() => dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'new', open: true } })}
                >
                    {(layout) => (
                        <LayoutInfoCard
                            id={`layout-card-${LAYOUT_MODE.MULTI}-${layout.id}`}
                            key={`${layout.id}-${LAYOUT_MODE.MULTI}`}
                            images={layout.images}
                            name={layout.name}
                            description={layout.description}
                            checked={viewMode ? undefined : selectedLayouts.includes(layout.id)}
                            onCheckedChange={
                                viewMode
                                    ? undefined
                                    : (isChecked) =>
                                          dispatch({
                                              type: 'TOGGLE_SELECTED_LAYOUT',
                                              payload: {
                                                  id: layout.id,
                                                  checked: isChecked,
                                              },
                                          })
                            }
                            sx={{ scrollMarginBlock: '200px' }}
                        >
                            <LayoutInfoCard.Chips>
                                <Chip size="small" label={layout.resolution} variant="outlined" color="primary" />
                                <Chip
                                    size="small"
                                    label={t('Layout No. {{number}}', { number: layout.number })}
                                    variant="outlined"
                                    color="primary"
                                />
                                {layout.colors.map((c, i) => (
                                    <ColorIndicator key={i} color={c} />
                                ))}
                            </LayoutInfoCard.Chips>
                            {!viewMode && (
                                <LayoutInfoCard.Actions>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <CardIconButton color="primary" onClick={handleEditChange(layout)}>
                                            <Edit />
                                        </CardIconButton>
                                        <CardIconButton color="error" onClick={handleDeleteChange(layout)}>
                                            <Delete />
                                        </CardIconButton>
                                    </Box>
                                </LayoutInfoCard.Actions>
                            )}
                        </LayoutInfoCard>
                    )}
                </AvailableLayoutsCard>
            </Box>
            <LayoutDialog
                open={state.dialogs.new && state.currentTab === LAYOUT_TYPE.MULTI}
                layoutNames={layoutNames}
                dialogTitle={t('New Layout')}
                onSave={handleAddNewLayout}
                onClose={() => dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'new', open: false } })}
            />
            <LayoutDialog
                open={state.dialogs.edit && state.currentTab === LAYOUT_TYPE.MULTI}
                layoutNames={layoutNames}
                dialogTitle={t('Edit Layout')}
                initialData={editData}
                onSave={handleUpdateLayout}
                onClose={() => {
                    dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'edit', open: false } });
                    dispatch({ type: 'CLEAR_EDIT_DATA' });
                }}
                showTopRightClose={false}
            />
        </>
    );
};
export default MultiLayout;
